import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { signUpSchema, SignUpSchemaType } from "../../domain/validation/auth.schema";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { Link } from "react-router-dom";
import { EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons";

interface SignUpFormProps {
  onSubmit: (data: SignUpSchemaType) => Promise<void>;
  isLoading?: boolean;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSubmit, isLoading = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpSchemaType>({
    resolver: zodResolver(signUpSchema),
    mode: "onChange",
  });

  const watchedFields = watch();
  const allFieldsFilled =
    watchedFields.firstName &&
    watchedFields.lastName &&
    watchedFields.password &&
    watchedFields.confirmPassword &&
    watchedFields.email &&
    watchedFields.organisationName &&
    watchedFields.smsConsent;

  return (
    <Card className="w-full max-w-3xl bg-white shadow-none border border-border-gray rounded-none">
      <CardHeader className="space-y-2 pb-6 pt-8">
        <CardTitle className="text-center text-xl font-bold uppercase tracking-wide">
          CREATE AN ACCOUNT
        </CardTitle>
        <CardDescription className="text-center text-sm text-gray-600">
          Fill in your details below to create a new account
        </CardDescription>
      </CardHeader>
      <CardContent className="px-10 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="relative pb-5">
              <Input
                placeholder="First Name"
                {...register("firstName")}
                error={!!errors.firstName}
                className="h-12 bg-disabled-bg border-border-gray placeholder:text-gray-500 rounded-none w-full"
                disabled={isLoading}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            <div className="relative pb-5">
              <Input
                placeholder="Last Name"
                {...register("lastName")}
                error={!!errors.lastName}
                className="h-12 bg-disabled-bg border-border-gray placeholder:text-gray-500 rounded-none w-full"
                disabled={isLoading}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="relative pb-5">
              <Input
                type="email"
                placeholder="E-mail"
                {...register("email")}
                error={!!errors.email}
                className="h-12 bg-disabled-bg border-border-gray placeholder:text-gray-500 rounded-none w-full"
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="relative pb-5">
              <Input
                placeholder="Practice Name"
                {...register("organisationName")}
                error={!!errors.organisationName}
                className="h-12 bg-disabled-bg border-border-gray placeholder:text-gray-500 rounded-none w-full"
                disabled={isLoading}
              />
              {errors.organisationName && (
                <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                  {errors.organisationName.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="relative pb-5">
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  {...register("password")}
                  error={!!errors.password}
                  className="h-12 bg-disabled-bg border-border-gray placeholder:text-gray-500 rounded-none w-full pr-10"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeClosedIcon className="h-5 w-5" />
                  ) : (
                    <EyeOpenIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="relative pb-5">
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  {...register("confirmPassword")}
                  error={!!errors.confirmPassword}
                  className="h-12 bg-disabled-bg border-border-gray placeholder:text-gray-500 rounded-none w-full pr-10"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeClosedIcon className="h-5 w-5" />
                  ) : (
                    <EyeOpenIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          <div className="relative pb-5">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="smsConsent"
                {...register("smsConsent")}
                disabled={isLoading}
                className="h-4 w-4 rounded border-gray-300 accent-primary-blue focus:ring-primary-blue cursor-pointer"
              />
              <label htmlFor="smsConsent" className="text-sm text-gray-600 cursor-pointer">
                I agree to receive SMS messages from Solentis Health, including updates and
                notifications.
              </label>
            </div>
            {errors.smsConsent && (
              <p className="text-xs text-red-500 absolute left-6 top-[calc(100%-1.25rem)]">
                {errors.smsConsent.message}
              </p>
            )}
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              className="w-full h-12 text-base bg-primary-blue hover:bg-primary-blue/90 text-white font-normal rounded-none disabled:cursor-not-allowed"
              disabled={isLoading || !allFieldsFilled}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
          </div>
        </form>

        <div className="flex justify-center pt-6">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <Link to="/login" className="text-link-blue hover:underline">
              Log In
            </Link>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
